import React, { useState, useEffect } from 'react';
import { Dropdown, Form, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import { LanguageProvider } from '../../../core/language-provider';
import { DataManager } from '../../../core/data-manager';
import './ColumnFilter.css';
import _ from "lodash";

export default function ColumnFilter({ 
    fieldName, 
    fieldConfig, 
    entity, 
    params,
    changeSorting,
    searchObj,
    setSearchObj
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const [filterValueMin, setFilterValueMin] = useState('');
    const [filterValueMax, setFilterValueMax] = useState('');
    const [filterType, setFilterType] = useState(
        (fieldConfig.input_type === 'option' || fieldConfig.input_type === 'externalEntity') ? 'in' : 'value'
    );
    const [activeFilter, setActiveFilter] = useState(null);

    // External entity states
    const [externalOptions, setExternalOptions] = useState([]);
    const [isLoadingExternal, setIsLoadingExternal] = useState(false);

    // Check if field is sortable
    const isSortable = () => {
        const nonSortable = ['richText', 'file', 'externalEntity', 'multipleExternalEntity', 'deepExternalEntity'];
        return !nonSortable.includes(fieldConfig.input_type);
    };

    // Check if field is filterable
    const isFilterable = () => {
        const nonFilterable = ['richText', 'file', 'multipleExternalEntity', 'deepExternalEntity'];
        return !nonFilterable.includes(fieldConfig.input_type);
    };

    // Load active filter from searchObj
    useEffect(() => {
        if (searchObj && searchObj.constraints) {
            const existingFilter = searchObj.constraints.find(c => c.fieldName === fieldName);
            if (existingFilter) {
                setActiveFilter(existingFilter);
                setFilterType(existingFilter.type);
                if (existingFilter.type === 'range') {
                    setFilterValueMin(existingFilter.min || '');
                    setFilterValueMax(existingFilter.max || '');
                    setFilterValue('');
                } else {
                    setFilterValue(existingFilter.value);
                    setFilterValueMin('');
                    setFilterValueMax('');
                }
            } else {
                setActiveFilter(null);
                setFilterValue('');
                setFilterValueMin('');
                setFilterValueMax('');
            }
        } else {
            setActiveFilter(null);
            setFilterValue('');
            setFilterValueMin('');
            setFilterValueMax('');
        }
    }, [searchObj, fieldName]);

    // Sort functions
    const handleSortAsc = () => {
        if (isActiveSorting && params.sortingOrder === 'asc') {
            // If clicking on already active ASC sort, reset to default (id DESC)
            changeSorting('id', 'desc');
        } else {
            changeSorting(fieldName, 'asc');
        }
        setIsOpen(false);
    };

    const handleSortDesc = () => {
        if (isActiveSorting && params.sortingOrder === 'desc') {
            // If clicking on already active DESC sort, reset to default (id DESC)
            changeSorting('id', 'desc');
        } else {
            changeSorting(fieldName, 'desc');
        }
        setIsOpen(false);
    };

    // Filter functions
    const applyFilter = () => {
        if (filterType === 'range') {
            if (!filterValueMin || !filterValueMax) return;
        } else if (filterType === 'isnull') {
            // isnull doesn't need a value
        } else {
            if (!filterValue || filterValue === '') return;
        }

        let newConstraint;
        if (filterType === 'range') {
            newConstraint = {
                fieldName: fieldName,
                type: filterType,
                min: filterValueMin,
                max: filterValueMax,
                range_type: 'STR'
            };
        } else if (filterType === 'isnull') {
            newConstraint = {
                fieldName: fieldName,
                type: filterType,
                value: null
            };
        } else {
            newConstraint = {
                fieldName: fieldName,
                type: filterType,
                value: filterValue
            };
        }

        let newConstraints = searchObj?.constraints ? [...searchObj.constraints] : [];
        
        // Remove existing filter for this field
        newConstraints = newConstraints.filter(c => c.fieldName !== fieldName);
        
        // Add new filter
        newConstraints.push(newConstraint);

        const newSearchObj = {
            ...searchObj,
            constraints: newConstraints
        };

        setSearchObj(newSearchObj);
        setIsOpen(false);
    };

    const clearFilter = () => {
        if (!searchObj?.constraints) return;

        const newConstraints = searchObj.constraints.filter(c => c.fieldName !== fieldName);
        
        const newSearchObj = {
            ...searchObj,
            constraints: newConstraints
        };

        setSearchObj(newSearchObj);
        setFilterValue('');
        setFilterValueMin('');
        setFilterValueMax('');
        setActiveFilter(null);
    };

    // External entity functions
    useEffect(() => {
        if (fieldConfig.input_type === 'externalEntity' && isOpen) {
            loadExternalOptions();
        }
    }, [isOpen, fieldConfig]);

    const loadExternalOptions = async () => {
        if (fieldConfig.input_type !== 'externalEntity') return;
        
        setIsLoadingExternal(true);
        const extEntity = fieldConfig.options.entity;
        const labelField = fieldConfig.options.labelField || 'name';
        
        try {
            const response = await DataManager.searchItemsSimple(extEntity, 1, 100, {
                'field': labelField,
                'order': 'asc'
            }, '', [labelField], []);
            
            if (response.success === 1) {
                const options = response.body.map(item => ({
                    value: item.id,
                    label: item[labelField] || `ID: ${item.id}`
                }));
                setExternalOptions(options);
            }
        } catch (error) {
            console.error('Error loading external options:', error);
        }
        setIsLoadingExternal(false);
    };

    const searchExternalOptions = async (searchTerm) => {
        if (!searchTerm || fieldConfig.input_type !== 'externalEntity') return;
        
        setIsLoadingExternal(true);
        const extEntity = fieldConfig.options.entity;
        const labelField = fieldConfig.options.labelField || 'name';
        
        try {
            const response = await DataManager.searchItemsSimple(extEntity, 1, 100, {
                'field': labelField,
                'order': 'asc'
            }, searchTerm, [labelField], []);
            
            if (response.success === 1) {
                const options = response.body.map(item => ({
                    value: item.id,
                    label: item[labelField] || `ID: ${item.id}`
                }));
                setExternalOptions(options);
            }
        } catch (error) {
            console.error('Error searching external options:', error);
        }
        setIsLoadingExternal(false);
    };

    const debouncedExternalSearch = React.useCallback(
        _.debounce((searchTerm) => {
            searchExternalOptions(searchTerm);
        }, 300),
        [fieldConfig]
    );

    // Get filter type options based on field type
    const getFilterTypes = () => {
        switch (fieldConfig.input_type) {
            case 'string':
            case 'text':
                return [
                    { value: 'value', label: LanguageProvider.get('search.equal') },
                    { value: 'like', label: LanguageProvider.get('search.contains') },
                    { value: 'start', label: LanguageProvider.get('search.begin_by') },
                    { value: 'end', label: LanguageProvider.get('search.end_by') },
                    { value: 'different_from_value', label: LanguageProvider.get('search.different') },
                    { value: 'isnull', label: LanguageProvider.get('search.isnull') }
                ];
            case 'number':
            case 'float':
                return [
                    { value: 'value', label: LanguageProvider.get('search.equal') },
                    { value: 'different_from_value', label: LanguageProvider.get('search.different') },
                    { value: 'minor', label: LanguageProvider.get('search.minor') },
                    { value: 'greater', label: LanguageProvider.get('search.greater') },
                    { value: 'isnull', label: LanguageProvider.get('search.isnull') }
                ];
            case 'date':
            case 'time':
            case 'datetime':
                return [
                    { value: 'value', label: LanguageProvider.get('search.equal') },
                    { value: 'different_from_value', label: LanguageProvider.get('search.different') },
                    { value: 'minor', label: LanguageProvider.get('search.minor') },
                    { value: 'greater', label: LanguageProvider.get('search.greater') },
                    { value: 'range', label: LanguageProvider.get('search.between') },
                    { value: 'isnull', label: LanguageProvider.get('search.isnull') }
                ];
            case 'boolean':
                return [
                    { value: 'value', label: LanguageProvider.get('search.equal') },
                    { value: 'different_from_value', label: LanguageProvider.get('search.different') },
                    { value: 'isnull', label: LanguageProvider.get('search.isnull') }
                ];
            case 'externalEntity':
                return [
                    { value: 'in', label: LanguageProvider.get('search.in') },
                    { value: 'not_in', label: LanguageProvider.get('search.not_in') },
                    { value: 'isnull', label: LanguageProvider.get('search.isnull') }
                ];
            case 'option':
                return [
                    { value: 'in', label: LanguageProvider.get('search.in') },
                    { value: 'isnull', label: LanguageProvider.get('search.isnull') }
                ];
            default:
                return [
                    { value: 'value', label: LanguageProvider.get('search.equal') },
                    { value: 'isnull', label: LanguageProvider.get('search.isnull') }
                ];
        }
    };

    // Render filter input based on field type
    const renderFilterInput = () => {
        // Don't render input for isnull filter type
        if (filterType === 'isnull') {
            return null;
        }

        switch (fieldConfig.input_type) {
            case 'string':
            case 'text':
                return (
                    <Form.Control
                        size="sm"
                        type="text"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') applyFilter();
                        }}
                        placeholder={LanguageProvider.get('filterColumns.filter_placeholder')}
                    />
                );
            
            case 'number':
            case 'float':
                return (
                    <Form.Control
                        size="sm"
                        type="number"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') applyFilter();
                        }}
                        placeholder={LanguageProvider.get('filterColumns.filter_placeholder')}
                    />
                );
            
            case 'date':
                if (filterType === 'range') {
                    return (
                        <>
                            <Form.Control
                                size="sm"
                                type="date"
                                value={filterValueMin}
                                onChange={(e) => setFilterValueMin(e.target.value)}
                                placeholder={LanguageProvider.get('search.from')}
                            />
                            <Form.Control
                                size="sm"
                                className='mt-1'
                                type="date"
                                value={filterValueMax}
                                onChange={(e) => setFilterValueMax(e.target.value)}
                                placeholder={LanguageProvider.get('search.to')}
                            />
                        </>
                    );
                } else {
                    return (
                        <Form.Control
                            size="sm"
                            type="date"
                            value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)}
                        />
                    );
                }
            
            case 'time':
                if (filterType === 'range') {
                    return (
                        <>
                            <Form.Control
                                size="sm"
                                type="time"
                                value={filterValueMin}
                                onChange={(e) => setFilterValueMin(e.target.value)}
                                placeholder={LanguageProvider.get('search.from')}
                            />
                            <Form.Control
                                size="sm"
                                className='mt-1'
                                type="time"
                                value={filterValueMax}
                                onChange={(e) => setFilterValueMax(e.target.value)}
                                placeholder={LanguageProvider.get('search.to')}
                            />
                        </>
                    );
                } else {
                    return (
                        <Form.Control
                            size="sm"
                            type="time"
                            value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)}
                        />
                    );
                }
            
            case 'datetime':
                if (filterType === 'range') {
                    return (
                        <>
                            <Form.Control
                                size="sm"
                                type="datetime-local"
                                value={filterValueMin}
                                onChange={(e) => setFilterValueMin(e.target.value)}
                                placeholder={LanguageProvider.get('search.from')}
                            />
                            <Form.Control
                                size="sm"
                                className='mt-1'
                                type="datetime-local"
                                value={filterValueMax}
                                onChange={(e) => setFilterValueMax(e.target.value)}
                                placeholder={LanguageProvider.get('search.to')}
                            />
                        </>
                    );
                } else {
                    return (
                        <Form.Control
                            size="sm"
                            type="datetime-local"
                            value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)}
                        />
                    );
                }
            
            case 'boolean':
                return (
                    <Form.Select
                        size="sm"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                    >
                        <option value="">{LanguageProvider.get('filterColumns.select_value')}</option>
                        <option value="1">{LanguageProvider.get('defaults.true')}</option>
                        <option value="0">{LanguageProvider.get('defaults.false')}</option>
                    </Form.Select>
                );
            
            case 'externalEntity':
                // Always use multi-select for externalEntity (only 'in' filter)
                // Parse filterValue as comma-separated string for multi-select
                const selectedIds = filterValue ? filterValue.split(',').map(v => v.trim()) : [];
                
                // Keep selected options even if not in current search results
                const selectedOptions = selectedIds.map(id => {
                    const found = externalOptions.find(opt => String(opt.value) === String(id));
                    if (found) return found;
                    // Return a basic option for selected items not in current results
                    return { value: id, label: `ID: ${id}` };
                }).filter(Boolean);
                
                // Merge selected options with search results, removing duplicates
                const allOptions = [...selectedOptions];
                externalOptions.forEach(opt => {
                    if (!selectedIds.includes(String(opt.value))) {
                        allOptions.push(opt);
                    }
                });
                
                return (
                    <Select
                        className="basic-multi-select"
                        classNamePrefix="select"
                        isMulti={true}
                        isLoading={isLoadingExternal}
                        isClearable={true}
                        isSearchable={true}
                        options={allOptions}
                        value={selectedOptions}
                        onChange={(selectedOptions) => {
                            const values = selectedOptions ? selectedOptions.map(opt => opt.value).join(',') : '';
                            setFilterValue(values);
                        }}
                        onInputChange={(inputValue, actionMeta) => {
                            if (actionMeta.action === 'input-change') {
                                debouncedExternalSearch(inputValue);
                            }
                        }}
                        placeholder={LanguageProvider.get('filterColumns.type_to_search')}
                        noOptionsMessage={({ inputValue }) => 
                            inputValue ? `${LanguageProvider.get('filterColumns.no_results_for')} "${inputValue}"` : LanguageProvider.get('filterColumns.start_typing')
                        }
                        styles={{
                            container: (base) => ({ ...base, minWidth: '200px' }),
                            control: (base) => ({ ...base, minHeight: '30px', fontSize: '10px' }),
                            option: (base) => ({ ...base, fontSize: '10px' }),
                            multiValue: (base) => ({ ...base, fontSize: '10px' }),
                            multiValueLabel: (base) => ({ ...base, fontSize: '10px' }),
                            placeholder: (base) => ({ ...base, fontSize: '10px' })
                        }}
                    />
                );
            
            case 'option':
                if (fieldConfig.options?.options) {
                    const options = fieldConfig.options.options.map(opt => ({
                        value: opt.value,
                        label: LanguageProvider.getLabel(entity, `${fieldName}.options.${opt.value}`)
                    }));
                    
                    // Parse filterValue as comma-separated string for multi-select
                    const selectedValues = filterValue ? filterValue.split(',').map(v => v.trim()) : [];
                    const selectedOptions = options.filter(opt => selectedValues.includes(opt.value));
                    
                    return (
                        <Select
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isMulti
                            options={options}
                            value={selectedOptions}
                            onChange={(selectedOptions) => {
                                const values = selectedOptions ? selectedOptions.map(opt => opt.value).join(',') : '';
                                setFilterValue(values);
                            }}
                            placeholder={LanguageProvider.get('filterColumns.select_option')}
                            isClearable={true}
                            styles={{
                                container: (base) => ({ ...base, minWidth: '200px' }),
                                control: (base) => ({ ...base, minHeight: '30px', fontSize: '10px' }),
                                option: (base) => ({ ...base, fontSize: '10px' }),
                                multiValue: (base) => ({ ...base, fontSize: '10px' }),
                                multiValueLabel: (base) => ({ ...base, fontSize: '10px' }),
                                placeholder: (base) => ({ ...base, fontSize: '10px' })
                            }}
                        />
                    );
                }
                return null;
            
            default:
                return (
                    <Form.Control
                        size="sm"
                        type="text"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') applyFilter();
                        }}
                        placeholder={LanguageProvider.get('filterColumns.filter_placeholder')}
                    />
                );
        }
    };

    // Check if column has active sorting
    const isActiveSorting = params.sortingField === fieldName;
    const sortIcon = params.sortingOrder === 'desc' ? 'chevron-down' : 'chevron-up';

    return (
        <div className="column-filter-header">
            <span className="me-2">{LanguageProvider.getLabel(entity, fieldName)}</span>
            
            
            {(isSortable() || isFilterable()) && (
                <Dropdown 
                    show={isOpen} 
                    onToggle={(nextShow) => setIsOpen(nextShow)}
                    align="end"
                >
                    <Dropdown.Toggle 
                        as="span"
                        role="button"
                        className={`column-filter-toggle ${(activeFilter || isActiveSorting) ? 'active-filter' : ''}`}
                        style={{ cursor: 'pointer', padding: '2px 6px' }}
                    >
                        <FontAwesomeIcon 
                            icon="ellipsis-v" 
                            size="sm" 
                            color={(activeFilter || isActiveSorting) ? "#0d6efd" : "#999999"}
                        />
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ minWidth: '250px' }}>
                        {isSortable() && (
                            <>
                                <Dropdown.Header>{LanguageProvider.get('filterColumns.sort')}</Dropdown.Header>
                                <Dropdown.Item onClick={handleSortAsc} className='small'>
                                    <FontAwesomeIcon icon="sort-alpha-down" className="me-2" />
                                    {LanguageProvider.get('filterColumns.sort_asc')}
                                    {isActiveSorting && params.sortingOrder === 'asc' && (
                                        <FontAwesomeIcon icon="check" className="ms-2 text-success" />
                                    )}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleSortDesc} className='small'>
                                    <FontAwesomeIcon icon="sort-alpha-up" className="me-2" />
                                    {LanguageProvider.get('filterColumns.sort_desc')}
                                    {isActiveSorting && params.sortingOrder === 'desc' && (
                                        <FontAwesomeIcon icon="check" className="ms-2 text-success" />
                                    )}
                                </Dropdown.Item>
                                {isFilterable() && <Dropdown.Divider />}
                            </>
                        )}
                        
                        {isFilterable() && (
                            <>
                                <Dropdown.Header>{LanguageProvider.get('filterColumns.filter')}</Dropdown.Header>
                                
                                {activeFilter && (
                                    <div className="px-3 mb-2">
                                        <Badge bg="info" className="w-100 text-start" style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>
                                            <span style={{ display: 'inline-block', maxWidth: 'calc(100% - 20px)' }}>
                                                {activeFilter.type === 'range'
                                                    ? `${LanguageProvider.get('search.between')} ${activeFilter.min} ${LanguageProvider.get('search.and')} ${activeFilter.max}`
                                                    : activeFilter.type === 'isnull'
                                                        ? LanguageProvider.get('search.isnull')
                                                        : `${activeFilter.type} = ${activeFilter.value}`
                                                }
                                            </span>
                                            <FontAwesomeIcon 
                                                icon="times" 
                                                className="ms-2 float-end"
                                                role="button"
                                                onClick={clearFilter}
                                            />
                                        </Badge>
                                    </div>
                                )}
                                
                                <div className="px-3 mb-2">
                                    <Form.Select
                                        size="sm"
                                        className="mb-2"
                                        value={filterType}
                                        onChange={(e) => {
                                            setFilterType(e.target.value);
                                            // Reset values when changing filter type
                                            setFilterValue('');
                                            setFilterValueMin('');
                                            setFilterValueMax('');
                                        }}
                                    >
                                        {getFilterTypes().map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    
                                    <div className="mb-2">
                                        {renderFilterInput()}
                                    </div>
                                    
                                    <div className="d-grid gap-2">
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={applyFilter}
                                            disabled={
                                                filterType === 'range'
                                                    ? (!filterValueMin || !filterValueMax)
                                                    : (filterType === 'isnull'
                                                        ? false
                                                        : (!filterValue || filterValue === ''))
                                            }
                                        >
                                            {LanguageProvider.get('filterColumns.apply_filter')}
                                        </Button>
                                        {activeFilter && (
                                            <Button 
                                                size="sm" 
                                                variant="outline-secondary"
                                                onClick={clearFilter}
                                            >
                                                {LanguageProvider.get('filterColumns.clear_filter')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            )}
        </div>
    );
}